export const timelineHighlights = [
  {
    value: '16k+',
    label: 'pages digitized',
    description: 'Depositions, flight manifests, FBI interviews, civil suits'
  },
  {
    value: '34',
    label: 'Palm Beach witnesses',
    description: 'Survivors interviewed by local detectives in 2005'
  },
  {
    value: '11',
    label: 'known flight logs',
    description: 'Documenting trips between New York, Palm Beach, and Little St. James'
  },
  {
    value: '2008',
    label: 'secret plea',
    description: 'Non-prosecution agreement that sparked a decade of scrutiny'
  }
];

export const timelinePhases = [
  {
    id: 'origins',
    title: 'Origins and Access',
    period: '1976–1995',
    theme: 'Wall Street polish masked alleged early recruitment patterns.',
    summary:
      'Epstein reportedly leveraged Dalton School connections to vault from college dropout to Wall Street fixer, cultivating Ghislaine Maxwell and a roster of billionaires while reports of underage massages began to emerge.',
    stats: [
      { label: 'Key locales', value: 'Dalton School, Manhattan, Palm Beach' },
      { label: 'Power patrons', value: 'Bear Stearns, Victoria’s Secret, the Maxwells' }
    ],
    spotlight: {
      label: 'Operating playbook',
      value: 'Flatter elites, promise capital, weaponize introductions.'
    },
    events: [
      {
        id: 'dalton-bear-stearns',
        year: '1976',
        headline: 'From Dalton to Bear Stearns',
        body:
          'Headmaster Donald Barr recruits Epstein to teach at the Dalton School; within months CEO Ace Greenberg taps him for Bear Stearns after wealthy parents rave about the charismatic math teacher.',
        quote: 'Greenberg later said Epstein had an "off the chart" ability to win trust.',
        searchQuery: '"Bear Stearns" Epstein Dalton School',
        media: {
          type: 'image',
          src: `${process.env.PUBLIC_URL || ''}/images/people/jeff2.avif`,
          alt: 'Young Jeffrey Epstein',
          caption: 'Epstein began cultivating relationships with wealthy parents while still teaching math.'
        }
      },
      {
        id: 'financial-consulting',
        year: '1981–1987',
        headline: 'Creates J. Epstein & Co.',
        body:
          'After a disputed insider-trading probe, he leaves Bear Stearns and forms his own offshore advisory shop, promising to manage only billionaires and securing clients through Leslie Wexner and other patrons.',
        quote: 'Court filings describe a "black box" fund run from St. Thomas and Manhattan.',
        searchQuery: '"J. Epstein & Co." Wexner'
      },
      {
        id: 'maxwell-alliance',
        year: '1991',
        headline: 'Partners with Ghislaine Maxwell',
        body:
          'Following Robert Maxwell\'s death, Ghislaine relocates to New York and reportedly becomes the gatekeeper to Epstein\'s social world, recruiting aspiring models and socialites for what were described as "massages" at the Manhattan townhouse.',
        quote: 'Witness statements describe Maxwell as "the scout, Epstein as the closer."',
        searchQuery: '"Ghislaine Maxwell" recruitment'
      }
    ],
    relatedDocs: [
      { label: 'Dalton correspondence', searchQuery: '"Dalton School" file_path' },
      { label: 'Wexner power of attorney', searchQuery: '"power of attorney" Epstein Wexner' }
    ]
  },
  {
    id: 'palm-beach',
    title: 'Palm Beach Pipeline',
    period: '1996–2005',
    theme: 'A mansion became a central location in the alleged trafficking ring.',
    summary:
      'With a private jet and Palm Beach estate, Epstein allegedly established a referral system of teenage girls, reportedly luring them with cash, modeling promises, and threats. Locals began whispering, but financial resources reportedly maintained the operation.',
    stats: [
      { label: 'Payments logged', value: '$200–$300 per visit' },
      { label: 'Drivers & staff', value: 'Butlers, pilots, recruiters' }
    ],
    spotlight: {
      label: 'Recruitment engine',
      value: 'One survivor would bring a friend, who reportedly brought others.'
    },
    events: [
      {
        id: 'palm-beach-mansion',
        year: '1996',
        headline: 'Palm Beach mansion reportedly outfitted for massages',
        body:
          'Epstein renovates the 36,000-square-foot Palm Beach home, reportedly adding massage tables on every floor and keeping cash, oils, and Polaroid cameras ready for a constant flow of minors.',
        searchQuery: '"Palm Beach" massage room'
      },
      {
        id: 'little-st-james',
        year: '1998',
        headline: 'Little St. James flights accelerate',
        body:
          'Flight manifests reveal frequent trips to the U.S. Virgin Islands, where staff described what they called "calendar girl" auditions and a reported constant presence of Maxwell and celebrity guests.',
        quote: 'A 2008 civil filing calls the island "the center of the operation."',
        searchQuery: '"Little St. James" flight log'
      },
      {
        id: 'survivor-tip',
        year: 'March 2005',
        headline: 'Survivor tip ignites investigation',
        body:
          'A 14-year-old tells Palm Beach police she was paid for a massage that she alleged turned abusive, triggering a months-long undercover probe that documents dozens of girls and detailed ledgers.',
        quote: 'Detective Joe Recarey: "This was a pyramid scheme of abuse."',
        searchQuery: '"Palm Beach police" affidavit'
      }
    ],
    relatedDocs: [
      { label: 'Palm Beach search warrant', searchQuery: '"search warrant" Palm Beach Epstein' },
      { label: 'Flight manifests', searchQuery: '"Flight Manifest" little st. james' }
    ]
  },
  {
    id: 'first-prosecution',
    title: 'First Prosecution and Non-Prosecution Agreement',
    period: '2005–2009',
    theme: 'Local detectives built the case; federal prosecutors entered a non-prosecution agreement.',
    summary:
      'Palm Beach police gathered 34 survivor statements, but the U.S. Attorney’s Office—led by Alex Acosta—negotiated a secret non-prosecution deal granting immunity to "co-conspirators" in exchange for a state plea.',
    stats: [
      { label: 'Victims in affidavits', value: '34 interviewed' },
      { label: 'Days served', value: '13 months of "work release"' }
    ],
    spotlight: {
      label: 'Why it mattered',
      value: 'The NPA effectively shielded recruiters and precluded federal charges for a decade.'
    },
    events: [
      {
        id: 'grand-jury-dispute',
        year: 'July 2006',
        headline: 'FBI builds federal case',
        body:
          'After Palm Beach prosecutors press only a single solicitation count, police chief Michael Reiter begs the FBI to intervene, producing a 53-page probable-cause memo and draft federal indictment.',
        searchQuery: '"53 page" probable cause memo Epstein',
        quote: 'Reiter told victims, "I apologize for the criminal justice system."'
      },
      {
        id: 'npa-signed',
        year: 'Sept 2007',
        headline: 'Secret Non-Prosecution Agreement',
        body:
          'Emails show prosecutors warning defense lawyers they must "keep this under wraps"; the deal immunized four named co-conspirators and promised the feds would not prosecute any potential accomplices.',
        searchQuery: '"non-prosecution agreement" Epstein'
      },
      {
        id: 'plea-and-release',
        year: 'June 2008',
        headline: 'Plea, work release, and NDAs',
        body:
          'Epstein pleads guilty in Florida state court to procuring minors for prostitution, serves 13 months mostly outside jail on "work release," and pays undisclosed settlements that include gag orders.',
        quote: 'Survivors stated they were never told the NPA existed—a violation a federal judge later confirmed.',
        searchQuery: '"Crime Victims Rights Act" Epstein'
      }
    ],
    relatedDocs: [
      { label: 'Non-prosecution agreement', searchQuery: '"NPA" "Epstein"' },
      { label: 'Crime Victims Rights Act ruling', searchQuery: '"Palm Beach" CVRA' }
    ]
  },
  {
    id: 'resurgence',
    title: 'Civil Suits and Renewed Reporting',
    period: '2010–2016',
    theme: 'Survivors sue while Epstein seeks to rebuild influence.',
    summary:
      'Even after registering as a sex offender, Epstein entertained politicians, funded science projects, and lobbied to downgrade his monitoring, while survivors like Virginia Giuffre filed defamation suits that reopened discovery.',
    stats: [
      { label: 'Civil cases filed', value: '20+ by 2016' },
      { label: 'Science donations', value: '$30M+ to labs and institutes' }
    ],
    spotlight: {
      label: 'Pressure points',
      value: 'Court unseals filings, exposing flight logs and new witnesses.'
    },
    events: [
      {
        id: 'civil-settlements',
        year: '2010',
        headline: 'Secret settlement wave',
        body:
          'Multiple Jane Does reportedly receive payouts tied to NDAs. A leaked spreadsheet tracks at least 19 settlements funded via offshore trusts and Wexner-linked accounts.',
        searchQuery: '"Jane Doe" settlement Epstein'
      },
      {
        id: 'giuffre-filing',
        year: '2014–2015',
        headline: 'Giuffre v. Maxwell filings',
        body:
          'Virginia Giuffre sues Ghislaine Maxwell for defamation, detailing alleged flights with Prince Andrew, Bill Clinton, and other VIPs; Judge Sweet orders the release of key deposition transcripts.',
        quote: 'Giuffre: "They trafficked me to powerful people to collect favors."',
        searchQuery: '"Giuffre v. Maxwell" deposition'
      },
      {
        id: 'monitoring-relief',
        year: '2013–2016',
        headline: 'Efforts to loosen sex-offender status',
        body:
          'Epstein petitions to reduce supervision in New York and the Virgin Islands, arguing he is "low risk"; internal NYPD memos reveal lobbying by celebrity lawyers.',
        searchQuery: '"risk level 3" Epstein'
      }
    ],
    relatedDocs: [
      { label: 'Giuffre deposition bundle', searchQuery: '"Giuffre" transcript' },
      { label: 'Flight log PDFs', searchQuery: '"Flight Log" PDF Epstein' }
    ]
  },
  {
    id: 'collapse',
    title: 'Collapse and Aftermath',
    period: '2017–2023',
    theme: 'Investigative journalism and new prosecutors finish the job.',
    summary:
      'The Miami Herald series, SDNY grand jury, and the global #MeToo context brought Epstein down. His 2019 arrest was followed by a controversial jail death ruled a suicide, Maxwell’s conviction, and hundreds of millions in restitution.',
    stats: [
      { label: 'SDNY charges', value: 'Sex trafficking + conspiracy' },
      { label: 'Restitution fund', value: '$121M to 150+ claimants' }
    ],
    spotlight: {
      label: 'Outstanding questions',
      value: 'Questions regarding the financing of the network, and which enablers still face exposure?'
    },
    events: [
      {
        id: 'miami-herald',
        year: 'Nov 2018',
        headline: 'Miami Herald publishes "Perversion of Justice"',
        body:
          'Reporter Julie K Brown interviews nearly 80 women, exposing the NPA and galvanizing public pressure that forced DOJ to review the case.',
        quote: 'Brown: "They were children—and no one would listen."',
        searchQuery: '"Perversion of Justice" documents'
      },
      {
        id: 'sdny-arrest',
        year: 'July 6, 2019',
        headline: 'SDNY arrests Epstein at Teterboro',
        body:
          'Agents seize passports, diamonds, and piles of cash from his Manhattan townhouse; prosecutors cite new victims and conspiracy counts that survived the NPA.',
        searchQuery: '"Teterboro" arrest warrant',
        media: {
          type: 'image',
          src: `${process.env.PUBLIC_URL || ''}/images/people/jeff.webp`,
          alt: 'Jeffrey Epstein booking photo',
          caption: '2019 mugshot entered in SDNY case 19-CR-490.'
        }
      },
      {
        id: 'jail-death',
        year: 'Aug 10, 2019',
        headline: 'Apparent jail suicide and fallout',
        body:
          'Epstein is found unresponsive in MCC New York amid reported camera failures and guard falsifications. Two correctional officers later admit to falsifying logs; public distrust explodes.',
        searchQuery: '"MCC" camera failure Epstein'
      },
      {
        id: 'maxwell-verdict',
        year: 'Dec 2021',
        headline: 'Ghislaine Maxwell convicted',
        body:
          'Federal jury finds Maxwell guilty on five counts of trafficking and conspiracy, validating survivor testimony and unsealing more than 10,000 pages of exhibits.',
        searchQuery: '"Maxwell trial exhibits"',
        media: {
          type: 'image',
          src: `${process.env.PUBLIC_URL || ''}/images/people/ghislaine.avif`,
          alt: 'Ghislaine Maxwell',
          caption: 'Trial transcripts detail how VIP networks enabled abuse.'
        }
      }
    ],
    relatedDocs: [
      { label: 'SDNY indictment 19-CR-490', searchQuery: '"19-cr-490" indictment' },
      { label: 'DOJ Inspector General review', searchQuery: '"Inspector General" MCC' },
      { label: 'Epstein Victims Compensation Program', searchQuery: '"EVCP" settlement' }
    ]
  }
];
